import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  googleSheetPageInteractionClickExportToSheet,
  googleSheetPageInteractionClickSelectSheet,
} from 'core/telemetry/avo/Avo';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { initialVatFieldValue, useVatCategories } from 'modules/menu/components/VatCategories';
import {
  batchValidatePartialCategories,
  batchValidatePartialOffers,
  batchValidatePartialOptions,
  batchImportPartialCategories,
  batchImportPartialOffers,
  batchImportPartialOptions,
} from 'modules/shared-menu/api';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useProductInformationSettings } from 'modules/shared-menu/hooks/useProductInformationSettings';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';
import { SharedMenu } from 'modules/shared-menu/types';

import { useWoltSpecials } from '../../SharedMenuEditorPage/context/WoltSpecialsContext';
import { BATCH_REQUEST_CHUNK_SIZE } from '../constants';
import { DownloadableSpreadsheet, SheetNameToShapedLinesMap, WithRowNumber, spreadsheet } from '../libs/googleSheets';
import { ShapelessLine } from '../libs/googleSheets/models';
import { ValidationResponse, ImportResponse, CategoryLine, OptionLine, OfferLine } from '../libs/googleSheets/types';
import {
  PermissionsValidations,
  buildNonAllowedFields,
  buildPercentages,
  buildPermissionsValidations,
  mergeErrors,
  removeInvalids,
  removeRowNumbers,
  reorderOffersByCategoriesOrder,
} from '../utils';

type UseGoogleSheetsImportProps = {
  sharedMenu: SharedMenu;
  onUploadProgressChange: React.Dispatch<React.SetStateAction<number>>;
  onRequestError: (message?: string) => void;
};

const QUERY_KEYS = Object.freeze({
  offers: (sharedMenuId: string) => ['shared-menu-spreadsheet-offers-download', sharedMenuId],
  options: (sharedMenuId: string) => ['shared-menu-spreadsheet-options-download', sharedMenuId],
  categories: (sharedMenuId: string) => ['shared-menu-spreadsheet-categories-download', sharedMenuId],
});

const promiseProgressWrapper = <T>(
  promises: Promise<T>[],
  onUploadProgressChange: UseGoogleSheetsImportProps['onUploadProgressChange'],
  maxPercentage: number,
) => {
  let count = 0;
  for (const p of promises) {
    p.then(() => {
      count++;
      const percentageCompleted = Math.floor((count * maxPercentage) / promises.length);
      onUploadProgressChange(oldPercentage => Math.min(100, oldPercentage + percentageCompleted));
    });
  }
  return Promise.all(promises);
};

// The multi choice options are not created when those values are not set
const prefilledOptionFields = {
  min: 0,
  max: 0,
};

export const useGoogleSheetsImport = ({
  sharedMenu,
  onUploadProgressChange,
  onRequestError,
}: UseGoogleSheetsImportProps) => {
  const { currency } = useCountryConfigContext();
  const menuFeatures = useOfferingMenuFeaturesContext();
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { data: vatCategories } = useVatCategories();
  const { woltSpecialsOfferIds, categoriesWithoutWoltSpecials } = useWoltSpecials();
  const { productInfoSettings } = useProductInformationSettings({ selectionId: sharedMenu.id });

  const getPrefilledOfferFields = useCallback<() => Partial<ShapelessLine>>(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { percentage: vat, categoryCode: vat_category } = initialVatFieldValue(
      vatCategories?.list,
      vatCategories?.default?.code,
    );

    return {
      vat,
      vat_category,
      delivery_methods: ['homedelivery', 'takeaway', 'eatin'].join(',\n'),
      enabled: 'true',
    };
  }, [vatCategories?.default?.code, vatCategories?.list]);

  /**
   * Note: this should later on be moved to a new file when we have the feature to select which fields to generate the spreadsheet with.
   *
   * So, upon rendering all the offer/option/category fields on this (to be created) screen/modal we should have a a list of checkboxes to select which fields to download. The `nonAllowedFieldsForDownload` should dictate which checkboxes are disabled on that screen - therefore making it clear for the user that they do not have access to the information of these specific fields.
   */
  const nonAllowedFields = useMemo(
    () => buildNonAllowedFields(menuFeatures, menuPermissions),
    [menuFeatures, menuPermissions],
  );

  const intl = useIntl();
  const [downloadableSpreadsheet, setDownloadableSpreadsheet] = useState<DownloadableSpreadsheet | null>(null);
  const [isGeneratingSpreadsheetFromMenu, setIsGeneratingSpreadsheetFromMenu] = useState(false);
  const [isStartedSelectingSheet, setIsStartedSelectingSheet] = useState(false);
  const shouldDownloadFromGoogle = useRef(true);
  const shouldValidate = useRef(true);
  const queryClient = useQueryClient();
  const { offers: existingMenuOffers, options: existingMenuOptions } = useSelectionData();

  const {
    data: offers,
    isInitialLoading: isOffersLoading,
    error: fromGoogleOfferError,
  } = useQuery(QUERY_KEYS.offers(sharedMenu.id), () => downloadableSpreadsheet?.downloadOffers(), {
    enabled: !!downloadableSpreadsheet && shouldDownloadFromGoogle.current,
    retry: false,
  });

  const {
    data: options,
    isInitialLoading: isOptionsLoading,
    error: fromGoogleOptionsError,
  } = useQuery(QUERY_KEYS.options(sharedMenu.id), () => downloadableSpreadsheet?.downloadOptions(), {
    enabled: !!downloadableSpreadsheet && shouldDownloadFromGoogle.current,
    retry: false,
  });

  const {
    data: categories,
    isInitialLoading: isCategoriesLoading,
    error: fromGoogleCategoriesError,
  } = useQuery(QUERY_KEYS.categories(sharedMenu.id), () => downloadableSpreadsheet?.downloadCategories(), {
    enabled: !!downloadableSpreadsheet && shouldDownloadFromGoogle.current,
    retry: false,
  });

  const isDoneDownloadingFromGoogle = useMemo(
    () =>
      !!(offers || fromGoogleOfferError) &&
      !!(options || fromGoogleOptionsError) &&
      !!(categories || fromGoogleCategoriesError),
    [categories, fromGoogleCategoriesError, fromGoogleOfferError, fromGoogleOptionsError, offers, options],
  );

  useEffect(() => {
    if (isDoneDownloadingFromGoogle) {
      shouldDownloadFromGoogle.current = false;
      googleSheetPageInteractionClickSelectSheet({
        numberOfOffers: (offers ?? []).length,
        numberOfOptions: (options ?? []).length,
        numberOfCategories: (categories ?? []).length,
      });
    }
  }, [categories, isDoneDownloadingFromGoogle, offers, options]);

  const permissionsValidationErrors = useMemo<PermissionsValidations | undefined>(() => {
    if (!isDoneDownloadingFromGoogle) {
      return;
    }

    return buildPermissionsValidations({
      offers: offers ?? [],
      options: options ?? [],
      categories: categories ?? [],
      nonAllowedFields,
    });
  }, [categories, isDoneDownloadingFromGoogle, nonAllowedFields, offers, options]);

  const initialPercentages = useMemo(
    () =>
      buildPercentages({
        offers,
        options,
        categories,
      }),
    [categories, offers, options],
  );

  const {
    data: offersValidation,
    isLoading: isOffersValidationLoading,
    mutate: executeOffersValidation,
    reset: resetOffersValidation,
  } = useMutation(
    async () => {
      const definedOffers = offers ?? [];
      const promises = [];
      for (
        let currentPage = 0;
        currentPage < Math.ceil(definedOffers.length / BATCH_REQUEST_CHUNK_SIZE);
        currentPage++
      ) {
        const sliced = definedOffers.slice(
          currentPage * BATCH_REQUEST_CHUNK_SIZE,
          (currentPage + 1) * BATCH_REQUEST_CHUNK_SIZE,
        );
        const offset = currentPage * BATCH_REQUEST_CHUNK_SIZE;
        const promise = batchValidatePartialOffers(sharedMenu.id, removeRowNumbers(sliced), offset);
        promises.push(promise);
      }

      const responses = await promiseProgressWrapper<ValidationResponse>(
        promises,
        onUploadProgressChange,
        initialPercentages.offers,
      );

      return { errors: responses.reduce((acc, response) => [...acc, ...response.errors], []) };
    },
    {
      onError: () =>
        onRequestError(
          intl.formatMessage({ id: 'shared-menu.editor.importer.toasts.error-validating' }, { model: 'Offers' }),
        ),
    },
  );

  const {
    data: optionsValidation,
    isLoading: isOptionsValidationLoading,
    mutate: executeOptionsValidation,
    reset: resetOptionsValidation,
  } = useMutation(
    async () => {
      const response = await batchValidatePartialOptions(sharedMenu.id, removeRowNumbers(options));
      onUploadProgressChange(oldPercentage => Math.min(100, oldPercentage + initialPercentages.options));
      return response;
    },
    {
      onError: () =>
        onRequestError(
          intl.formatMessage({ id: 'shared-menu.editor.importer.toasts.error-validating' }, { model: 'Options' }),
        ),
    },
  );

  const {
    data: categoriesValidation,
    isLoading: isCategoriesValidationLoading,
    mutate: executeCategoriesValidation,
    reset: resetCategoriesValidation,
  } = useMutation(
    async () => {
      const response = await batchValidatePartialCategories(sharedMenu.id, removeRowNumbers(categories));
      onUploadProgressChange(oldPercentage => Math.min(100, oldPercentage + initialPercentages.categories));
      return response;
    },
    {
      onError: () =>
        onRequestError(
          intl.formatMessage({ id: 'shared-menu.editor.importer.toasts.error-validating' }, { model: 'Categories' }),
        ),
    },
  );

  const isDoneValidating = useMemo(
    () => !!offersValidation && !!optionsValidation && !!categoriesValidation,
    [categoriesValidation, offersValidation, optionsValidation],
  );

  const isEmptySpreadsheet = useMemo(
    () => isDoneValidating && !offers?.length && !options?.length && !categories?.length,
    [categories, isDoneValidating, offers, options],
  );

  useEffect(() => {
    if (isDoneValidating) {
      shouldValidate.current = false;
    }
  }, [isDoneValidating]);

  useEffect(() => {
    if (isDoneDownloadingFromGoogle && shouldValidate.current) {
      executeOffersValidation();
      executeOptionsValidation();
      executeCategoriesValidation();
    }
  }, [executeCategoriesValidation, executeOffersValidation, executeOptionsValidation, isDoneDownloadingFromGoogle]);

  const isDownloadingAndValidating =
    isOffersLoading ||
    isOptionsLoading ||
    isCategoriesLoading ||
    isOffersValidationLoading ||
    isOptionsValidationLoading ||
    isCategoriesValidationLoading;

  /**
   * API validation errors + user permissions validation errors
   */
  const mergedErrors = useMemo(
    () => ({
      offers: mergeErrors(permissionsValidationErrors?.offers, offersValidation?.errors),
      options: mergeErrors(permissionsValidationErrors?.options, optionsValidation?.errors),
      categories: mergeErrors(permissionsValidationErrors?.categories, categoriesValidation?.errors),
    }),
    [
      categoriesValidation?.errors,
      offersValidation?.errors,
      optionsValidation?.errors,
      permissionsValidationErrors?.categories,
      permissionsValidationErrors?.offers,
      permissionsValidationErrors?.options,
    ],
  );

  /**
   * Entities (offers/options/categories) to be used in the final payload
   */
  const validEntities = useMemo<{
    offers: WithRowNumber<Partial<OfferLine>>[];
    options: WithRowNumber<Partial<OptionLine>>[];
    categories: WithRowNumber<Partial<CategoryLine>>[];
  }>(() => {
    const exclusionMap = {
      offers: new Map(mergedErrors.offers.map(error => [error.line, true])),
      options: new Map(mergedErrors.options.map(error => [error.line, true])),
      categories: new Map(mergedErrors.categories.map(error => [error.line, true])),
    };

    return {
      offers: removeInvalids(offers ?? [], exclusionMap.offers),
      options: removeInvalids(options ?? [], exclusionMap.options),
      categories: removeInvalids(categories ?? [], exclusionMap.categories),
    };
  }, [categories, mergedErrors.categories, mergedErrors.offers, mergedErrors.options, offers, options]);

  const finalPayloadPercentages = useMemo(
    () =>
      buildPercentages({
        offers: validEntities.offers,
        options: validEntities.options,
        categories: validEntities.categories,
      }),
    [validEntities.categories, validEntities.offers, validEntities.options],
  );

  const { mutateAsync: executeBatchImportOffers, isLoading: isUploadOffersLoading } = useMutation(async () => {
    const promises = [];

    for (
      let currentPage = 0;
      currentPage < Math.ceil(validEntities.offers.length / BATCH_REQUEST_CHUNK_SIZE);
      currentPage++
    ) {
      const sliced = validEntities.offers.slice(
        currentPage * BATCH_REQUEST_CHUNK_SIZE,
        (currentPage + 1) * BATCH_REQUEST_CHUNK_SIZE,
      );
      const offset = currentPage * BATCH_REQUEST_CHUNK_SIZE;
      const promise = batchImportPartialOffers(sharedMenu.id, removeRowNumbers(sliced), offset);
      promises.push(promise);
    }

    const responses = await promiseProgressWrapper<ImportResponse<Offer>>(
      promises,
      onUploadProgressChange,
      finalPayloadPercentages.offers,
    );

    return responses.reduce(
      (acc, response) => {
        return {
          errors: [...acc.errors, ...response.errors],
          imported_items: [...acc.imported_items, ...response.imported_items],
        };
      },
      { errors: [], imported_items: [] },
    );
  });

  const { mutateAsync: executeBatchImportOptions, isLoading: isUploadOptionsLoading } = useMutation(async () => {
    const response = await batchImportPartialOptions(sharedMenu.id, removeRowNumbers(validEntities.options));
    onUploadProgressChange(oldPercentage => Math.min(100, oldPercentage + finalPayloadPercentages.options));
    return response;
  });

  const { mutateAsync: executeBatchImportCategories, isLoading: isUploadCategoriesLoading } = useMutation(async () => {
    const response = batchImportPartialCategories(sharedMenu.id, removeRowNumbers(validEntities.categories));
    onUploadProgressChange(oldPercentage => Math.min(100, oldPercentage + finalPayloadPercentages.categories));
    return response;
  });

  const isUploading = isUploadOffersLoading || isUploadOptionsLoading || isUploadCategoriesLoading;

  const uploadData = useCallback(
    async ({ onSuccess, onError }: { onSuccess: () => void; onError: () => void }) => {
      try {
        await executeBatchImportOffers();
        await executeBatchImportCategories();
        await executeBatchImportOptions();
        onSuccess();
      } catch {
        onError();
      }
    },
    [executeBatchImportCategories, executeBatchImportOffers, executeBatchImportOptions],
  );

  const openSpreadsheetPickerModal = useCallback(async () => {
    setIsStartedSelectingSheet(true);
    const spreadsheetRef = await spreadsheet({
      spreadsheetOptions: {
        lang: sharedMenu?.primary_language,
        currency,
        prefilledFields: { offer: getPrefilledOfferFields(), option: prefilledOptionFields },
        productHierarchies: productInfoSettings?.product_hierarchy_levels ?? [],
      },
    }).select();
    setDownloadableSpreadsheet(spreadsheetRef);
    setIsStartedSelectingSheet(false);
  }, [sharedMenu?.primary_language, currency, getPrefilledOfferFields, productInfoSettings?.product_hierarchy_levels]);

  const createSpreadsheetFromExistingMenu = useCallback(
    async ({ onError, selectedFields }: { onError: () => void; selectedFields: SheetNameToShapedLinesMap }) => {
      const offersWithoutWoltSpecials = existingMenuOffers?.filter(offer => !woltSpecialsOfferIds.has(offer.id));

      googleSheetPageInteractionClickExportToSheet({
        numberOfOffers: (offersWithoutWoltSpecials ?? []).length,
        numberOfOptions: (existingMenuOptions ?? []).length,
        numberOfCategories: (categoriesWithoutWoltSpecials ?? []).length,
      });

      setIsGeneratingSpreadsheetFromMenu(true);

      const reorderedOffers = reorderOffersByCategoriesOrder(offersWithoutWoltSpecials, categoriesWithoutWoltSpecials);

      await spreadsheet({
        spreadsheetOptions: {
          lang: sharedMenu?.primary_language ?? 'en',
          selectedFields,
          productHierarchies: productInfoSettings?.product_hierarchy_levels ?? [],
        },
      })
        .attachCategories(categoriesWithoutWoltSpecials)
        .attachOffers(reorderedOffers)
        .attachOptions(existingMenuOptions)
        .notEmptyOrSampleSpreadsheet()
        .export()
        .then(spreadsheet => {
          return spreadsheet?.spreadsheetUrl;
        })
        .then(url => {
          setIsGeneratingSpreadsheetFromMenu(false);
          window.open(url, '_blank')?.focus();
        })
        .catch(() => {
          setIsGeneratingSpreadsheetFromMenu(false);
          onError();
        });
    },
    [
      categoriesWithoutWoltSpecials,
      existingMenuOffers,
      existingMenuOptions,
      sharedMenu?.primary_language,
      woltSpecialsOfferIds,
      productInfoSettings?.product_hierarchy_levels,
    ],
  );

  const createSpreadsheetForSelectedOffers = useCallback(
    async ({
      onError,
      selectedFields,
      selectedOfferIds,
    }: {
      onError: () => void;
      selectedFields: SheetNameToShapedLinesMap;
      selectedOfferIds: Set<string>;
    }) => {
      setIsGeneratingSpreadsheetFromMenu(true);

      const selectedOffers = existingMenuOffers?.filter(offer => selectedOfferIds.has(offer.id));

      googleSheetPageInteractionClickExportToSheet({
        numberOfOffers: selectedOffers.length,
        numberOfOptions: 0,
        numberOfCategories: 0,
      });

      await spreadsheet({
        spreadsheetOptions: {
          lang: sharedMenu?.primary_language ?? 'en',
          selectedFields,
          productHierarchies: productInfoSettings?.product_hierarchy_levels ?? [],
        },
      })
        .attachOffers(selectedOffers)
        .export()
        .then(spreadsheet => {
          return spreadsheet?.spreadsheetUrl;
        })
        .then(url => {
          setIsGeneratingSpreadsheetFromMenu(false);
          window.open(url, '_blank')?.focus();
        })
        .catch(() => {
          setIsGeneratingSpreadsheetFromMenu(false);
          onError();
        });
    },
    [existingMenuOffers, productInfoSettings?.product_hierarchy_levels, sharedMenu?.primary_language],
  );

  const createSpreadsheetWithSampleData = useCallback(
    async ({ onError }: { onError: () => void }) => {
      setIsGeneratingSpreadsheetFromMenu(true);

      await spreadsheet({
        spreadsheetOptions: {
          lang: sharedMenu?.primary_language ?? 'en',
          productHierarchies: productInfoSettings?.product_hierarchy_levels ?? [],
        },
      })
        .notEmptyOrSampleSpreadsheet()
        .export()
        .then(spreadsheet => {
          return spreadsheet?.spreadsheetUrl;
        })
        .then(url => {
          setIsGeneratingSpreadsheetFromMenu(false);
          window.open(url, '_blank')?.focus();
        })
        .catch(() => {
          setIsGeneratingSpreadsheetFromMenu(false);
          onError();
        });
    },
    [productInfoSettings?.product_hierarchy_levels, sharedMenu?.primary_language],
  );

  const resetProcess = useCallback(() => {
    setDownloadableSpreadsheet(null);
    shouldValidate.current = true;
    shouldDownloadFromGoogle.current = true;
    Object.values(QUERY_KEYS).forEach(getQueryKeyFn => {
      queryClient.removeQueries({ queryKey: getQueryKeyFn(sharedMenu.id) });
    });
    resetOffersValidation();
    resetOptionsValidation();
    resetCategoriesValidation();
  }, [queryClient, resetCategoriesValidation, resetOffersValidation, resetOptionsValidation, sharedMenu.id]);

  return {
    isDownloadingAndValidating,
    isUploading,
    isGeneratingSpreadsheetFromMenu,
    isStartedSelectingSheet,
    openSpreadsheetPickerModal,
    createSpreadsheetFromExistingMenu,
    createSpreadsheetForSelectedOffers,
    createSpreadsheetWithSampleData,
    selectedSpreadsheetFile: downloadableSpreadsheet?.spreadsheet,
    isDoneValidating,
    isEmptySpreadsheet,
    uploadData,
    offers: { data: offers ?? [], errors: mergedErrors.offers },
    options: { data: options ?? [], errors: mergedErrors?.options },
    categories: {
      data: categories ?? [],
      errors: mergedErrors?.categories,
    },
    resetProcess,
  };
};
